import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  increment,
  deleteDoc,
  getFirestore,
} from "firebase/firestore";
import RequestNotice from "../components/RequestNotice";
import { isFinalStatus, notifyRequestOutcome } from "../utils/requestNotify";

function AssignmentsPage() {
  const db = getFirestore(); // Initialize Firestore

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundCoins, setRefundCoins] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const fetchRefundCoins = async () => {
      const adminDoc = await getDoc(doc(db, "adminPanel", "admin"));
      if (adminDoc.exists()) {
        setRefundCoins(adminDoc.data().assignmentSubmissionCost || 0);
      } else {
        console.log("No such document!");
      }
    };

    const fetchAssignments = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "questions"));
        const assignmentsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAssignments(assignmentsData);
      } catch (error) {
        console.error("Error fetching assignments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRefundCoins();
    fetchAssignments();
  }, [db]);

  // Emails the user about the outcome and shows the admin whether it went out.
  const sendNotification = async (id, refundedCoins = 0) => {
    const result = await notifyRequestOutcome("assignment", id, refundedCoins);
    if (!result.ok) {
      setNotice({ type: "error", text: `Status saved, but the email was not sent: ${result.message}` });
      return;
    }
    setAssignments((prev) => prev.map((item) => (item.id === id ? { ...item, notifiedStatus: item.status } : item)));
    setNotice({ type: "success", text: result.already ? "The user was already emailed about this." : "Status saved and the user was emailed." });
  };

  const handleStatusChange = async (id, newStatus, userId = null) => {
    try {
      const assignmentDoc = doc(db, "questions", id);
      await updateDoc(assignmentDoc, { status: newStatus });

      if (newStatus === "declined" && refundCoins > 0 && userId) {
        const userDoc = doc(db, "users", userId);
        await updateDoc(userDoc, { coins: increment(refundCoins) });
      }

      setAssignments((prevRequests) =>
        prevRequests.map((request) =>
          request.id === id ? { ...request, status: newStatus } : request,
        ),
      );
      await sendNotification(id, newStatus === "declined" && refundCoins > 0 && userId ? refundCoins : 0);
    } catch (error) {
      console.error("Error updating assignment status:", error);
      setNotice({ type: "error", text: "Could not update the status. Please try again." });
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm("Delete this assignment request?")) return;

    try {
      await deleteDoc(doc(db, "questions", id));
      setAssignments((prevAssignments) =>
        prevAssignments.filter((assignment) => assignment.id !== id),
      );
    } catch (error) {
      console.error("Error deleting assignment:", error);
    }
  };

  const statusCounts = {
    all: assignments.length,
    pending: assignments.filter(
      (item) => item.status?.toLowerCase() === "pending",
    ).length,
    completed: assignments.filter(
      (item) => item.status?.toLowerCase() === "completed",
    ).length,
    declined: assignments.filter(
      (item) => item.status?.toLowerCase() === "declined",
    ).length,
  };

  const filteredAssignments = assignments
    .filter((assignment) => {
      if (statusFilter === "all") return true;
      return (assignment.status || "").toLowerCase() === statusFilter;
    })
    .filter((assignment) => {
      const query = searchQuery.toLowerCase();
      return (
        assignment.email?.toLowerCase().includes(query) ||
        assignment.question?.toLowerCase().includes(query) ||
        assignment.comment?.toLowerCase().includes(query) ||
        assignment.status?.toLowerCase().includes(query) ||
        assignment.pages?.toString().includes(query)
      );
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-lg text-gray-600">Loading assignments...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        Assignments
      </h1>
      <RequestNotice notice={notice} onDismiss={() => setNotice(null)} />
      {/* Search Input */}
      <div className="mb-4">
        <div className="mb-4 text-center text-sm text-gray-600">
          <span className="mr-3">Pending: {statusCounts.pending}</span>
          <span className="mr-3">Completed: {statusCounts.completed}</span>
          <span>Declined: {statusCounts.declined}</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { key: "all", label: `All (${statusCounts.all})` },
            { key: "pending", label: `Pending (${statusCounts.pending})` },
            {
              key: "completed",
              label: `Completed (${statusCounts.completed})`,
            },
            { key: "declined", label: `Declined (${statusCounts.declined})` },
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setStatusFilter(filter.key)}
              className={`px-3 py-2 rounded-md border transition duration-200 ${
                statusFilter === filter.key
                  ? "bg-purple-800 text-white border-purple-800"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search assignments by email, question, comment, or status"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
        />
      </div>
      {filteredAssignments.length === 0 ? (
        <p className="text-gray-600 text-center">No assignments found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-purple-800 text-white">
                <th className="px-4 py-3 text-left text-sm font-medium">
                  User Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Question
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Pages
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Comment
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map((assignment, index) => (
                <tr
                  key={assignment.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-gray-100 transition duration-200`}
                >
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {assignment.email}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {assignment.question}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {assignment.pages}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {assignment.comment}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                    {assignment.status}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {assignment.status === "pending" ? (
                      <div className="flex flex-col sm:flex-row sm:space-x-2 gap-2">
                        <button
                          onClick={() =>
                            handleStatusChange(
                              assignment.id,
                              "completed",
                              assignment.userId,
                            )
                          }
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
                        >
                          Complete
                        </button>
                        <button
                          onClick={() =>
                            handleStatusChange(
                              assignment.id,
                              "declined",
                              assignment.userId,
                            )
                          }
                          className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-200"
                        >
                          Decline
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row sm:space-x-2 gap-2">
                        {isFinalStatus(assignment.status) && assignment.notifiedStatus !== assignment.status && (
                          <button
                            onClick={() => sendNotification(assignment.id)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
                          >
                            Send email
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteAssignment(assignment.id)}
                          className="bg-gray-700 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition duration-200"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AssignmentsPage;
